import type { ReactNode } from 'react';

/**
 * Inline markdown for lesson prose — bold, italic, code, nothing else.
 *
 * The 63 drafted grammar lessons write emphasis the way a model writes it (`**-ები**`, `` `ში` ``)
 * and until 2026-09-03 every screen rendered the markers as literal asterisks — syntax on the one
 * surface whose job is teaching. This renders exactly the inline set the drafts use. Deliberately
 * NOT a markdown parser: headings are refused by the draft validator, links have no place in a
 * lesson body, and a "- " list line reads fine under the pre-wrap the body already gets. If the
 * drafts ever grow past this set, grow the validator first — an unrendered marker is a visible
 * bug, a silently-swallowed one is worse.
 *
 * `<code>` inherits the font on purpose: the fragments inside backticks are Georgian affixes, and
 * a monospace fallback would break script shaping for a one-glyph "win".
 */
// Emphasis may not start or end on whitespace — "a * b and * c" is arithmetic, not italics.
const INLINE = /(\*\*\S(?:[^*\n]*\S)?\*\*|`[^`\n]+`|\*\S(?:[^*\n]*\S)?\*)/g;

export function renderInline(text: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code
          key={i}
          style={{
            fontFamily: 'inherit',
            background: 'color-mix(in srgb, var(--nn-text) 8%, transparent)',
            borderRadius: 3,
            padding: '0 3px',
          }}
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}
